import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import Img from "./img";
import { useUI, useAppSession, useCategory } from "../contexts";
import { useSearch } from "@/contexts/SearchProvider";
import { dasherize } from "../utils/dom";

const CategoryNav = ({
	url,
	categoryGroupId,
	catName,
	setRecentCategory,
	category, // Pass the full category object
}) => {
	const { setCurrentCategoryGroupId, currentCategoryGroupId } = useSearch();
	return (
		<Link
			href={url}
			onClick={() => {
				setCurrentCategoryGroupId(categoryGroupId);
				// A real category goes to the front of the bar next time. For You
				// and All pass no category and are not remembered.
				if (category) setRecentCategory(category);
			}}
			className={`nav-category-item ${
				currentCategoryGroupId === categoryGroupId ? "currentCat" : ""
			}`}
		>
			{catName}
		</Link>
	);
};

const NavCategoryBar = () => {
	const { setCategoryDrawerOpen, categoryDrawerOpen } = useUI();
	const { setRecentCategory, recentCategories } = useSearch();
	const { clientSide } = useAppSession();
	const { visibleCategories, loading, getCategoryById } = useCategory();
	const scrollerRef = useRef<HTMLDivElement>(null);
	const [hasMoreRight, setHasMoreRight] = useState(false);

	// Only listened to while the scroller is mounted (the effect below adds and
	// removes it), so the ref is set whenever this runs.
	const updateChevron = useCallback(() => {
		const el = scrollerRef.current as HTMLDivElement;
		setHasMoreRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 8);
	}, []);

	useEffect(() => {
		const el = scrollerRef.current;
		if (!el) return;
		updateChevron();
		el.addEventListener("scroll", updateChevron, { passive: true });
		window.addEventListener("resize", updateChevron);
		return () => {
			el.removeEventListener("scroll", updateChevron);
			window.removeEventListener("resize", updateChevron);
		};
	}, [updateChevron, loading, clientSide, visibleCategories, recentCategories]);

	// The chevron is drawn inside the scroller's row, so the ref is set.
	const scrollRight = () => {
		const el = scrollerRef.current as HTMLDivElement;
		el.scrollBy({ left: el.clientWidth * 0.8, behavior: "smooth" });
	};

	if (!clientSide) return null;
	if (loading) return <div className="nav-category-bar"><div className="nav-category-bar-inner">Loading categories...</div></div>;

	// Build the complete category list
	// Start with recent categories, then add remaining visible categories
	const categoryList = [];
	const addedIds = new Set();

	// First, add recent categories (these appear first)
	recentCategories?.forEach(recentCat => {
		if (recentCat.id === 31) return; // Skip Jobs for now
		if (recentCat.id === 0) return; // Skip "All", we handle it separately

		// Get the full category data from the provider to ensure it's up to date
		const category = getCategoryById(recentCat.id);
		if (category) {
			const url = `/browse/${category.slug || dasherize(category.label)}/`;
			categoryList.push(
				<CategoryNav
					url={url}
					categoryGroupId={category.id}
					catName={category.label}
					setRecentCategory={setRecentCategory}
					category={category}
					key={`cat-${category.id}`}
				/>
			);
			addedIds.add(category.id);
		}
	});

	// Then add remaining visible categories that aren't in recent
	visibleCategories.forEach(visibleCatId => {
		if (visibleCatId === 0) return; // Skip "All", we handle it separately
		if (visibleCatId === 31) return; // Skip Jobs for now
		if (addedIds.has(visibleCatId)) return; // Skip if already added as recent

		const category = getCategoryById(visibleCatId);
		if (category) {
			const url = `/browse/${category.slug || dasherize(category.label)}/`;
			categoryList.push(
				<CategoryNav
					url={url}
					categoryGroupId={category.id}
					catName={category.label}
					setRecentCategory={setRecentCategory}
					category={category}
					key={`cat-${category.id}`}
				/>
			);
			addedIds.add(category.id);
		}
	});

	return (
		<nav className="nav-category-bar">
			<div className="nav-category-bar-inner">
				<div
					className="nav-menu-burger"
					onClick={() => {
						setCategoryDrawerOpen(!categoryDrawerOpen);
					}}
				>
					<Img src="/images/svg/hamburger-menu.svg" />
				</div>
				<div className="nav-category-bar-inner2" ref={scrollerRef}>
					<div className="nav-category-scroller">
						<CategoryNav
							url={"/foryou"}
							categoryGroupId={-1}
							catName={"For You"}
							setRecentCategory={setRecentCategory}
							category={null}
						/>
						<CategoryNav
							url={"/"}
							categoryGroupId={0}
							catName={"All"}
							setRecentCategory={setRecentCategory}
							category={null}
						/>
						{categoryList}
					</div>
				</div>
				{hasMoreRight && (
					<button
						type="button"
						className="nav-category-chevron"
						aria-label="Scroll categories right"
						onClick={scrollRight}
					>
						<svg
							width="14"
							height="14"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="3"
							strokeLinecap="round"
							strokeLinejoin="round"
							aria-hidden="true"
						>
							<polyline points="9 6 15 12 9 18" />
						</svg>
					</button>
				)}
			</div>
		</nav>
	);
};

export default NavCategoryBar;
